import { createAsyncThunk } from '@reduxjs/toolkit';
import i18next from 'i18next';

import {
  isEmptyDataPropertyWithFilter,
  removeCurrentProperty,
  setCurrentProperty,
  setLoadingProperty,
} from '@pages/hubs/Property/store/property-slice';
import api from '@setup/api';
import { handleServerError } from '@setup/config/serverErrorHandler';
import { NotificationService } from '@setup/notifications/SnackbarUtilsConfigurator/SnackbarUtilsConfigurator';
import { StatusServerErrors } from '@shared/enums/status-server-errors';

import type { EntityProperty } from '../Property';
import type { RootMeta } from '@pages/hubs/Garage/store/types';
import type { GetHubsParams } from '@pages/hubs/Pets/store/pets-types';
import type { PropertyType } from '@pages/hubs/Property/store/property-types';
import type { ErrorType, ServerErrorType } from '@shared/interfaces/error.interfaces';
import type { EntityHubSocket } from '@shared/models/hubs/hub-socket-response';
import type { AppDispatch, RootState } from '@store/store';

export const getProperty = createAsyncThunk<PropertyType, number, { rejectValue: ErrorType }>(
  'getProperty',
  async (propertyID, { rejectWithValue, dispatch }) => {
    try {
      const property = await api.property.getProperty(propertyID);
      dispatch(setCurrentProperty({ property }));
      return property;
    } catch (e: any) {
      const axiosError = e as ServerErrorType;
      handleServerError(
        e,
        {
          [StatusServerErrors.AccessDenied]: {
            message: i18next.t('general.notifications.propertyNotSharedWithYou'),
          },
          [StatusServerErrors.ItemNotFound]: {
            message: i18next.t('general.notifications.propertyNotFound'),
          },
        },
        {
          notificationVariant: 'info',
        },
      );

      return rejectWithValue(axiosError.data);
    }
  },
);
export const createProperty = createAsyncThunk<
  PropertyType,
  Partial<PropertyType>,
  { rejectValue: ErrorType }
>('createProperty', async (data, { rejectWithValue }) => {
  try {
    return await api.property.createProperty(data);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e, {}, { notify: axiosError.status !== StatusServerErrors.ValidationException });
    return rejectWithValue(axiosError.data);
  }
});

export const updateProperty = createAsyncThunk<
  PropertyType,
  { data: Omit<Partial<PropertyType>, 'residents'> & { residents: { id: number }[] }; propertyID: number },
  { rejectValue: ErrorType }
>('updateProperty', async ({ data, propertyID }, { rejectWithValue }) => {
  try {
    return await api.property.updateProperty(data, propertyID);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e, {}, { notify: axiosError.status !== StatusServerErrors.ValidationException });
    return rejectWithValue(axiosError.data);
  }
});

export const removeProperty = createAsyncThunk<any, { propertyID: number }, { rejectValue: ErrorType }>(
  'removeProperty',
  async ({ propertyID }, { rejectWithValue, dispatch }) => {
    try {
      return await api.property.removeProperty(propertyID).then(() => {
        dispatch(removeCurrentProperty({ propertyID }));
        NotificationService.success(i18next.t('property.notifications.removeProperty'));
      });
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(e);
      return rejectWithValue(axiosError.data);
    }
  },
);

export const getProperties = createAsyncThunk<
  { data: PropertyType[]; meta: RootMeta },
  Partial<GetHubsParams>,
  { rejectValue: ErrorType; dispatch: AppDispatch; state: RootState }
>('getProperties', async (propertyParams, { dispatch, rejectWithValue }) => {
  dispatch(setLoadingProperty({ isLoading: false }));
  try {
    const { data, meta } = await api.property.getProperties({
      page: propertyParams.page || 1,
      ...propertyParams,
    });
    dispatch(isEmptyDataPropertyWithFilter({ filters: propertyParams, propertyData: data }));

    return {
      data,
      meta,
    };
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  } finally {
    dispatch(setLoadingProperty({ isLoading: true }));
  }
});

// ---------------- web-socket ----------------
export const updatePropertyWS = createAsyncThunk<
  {
    propertyWS: PropertyType;
  },
  EntityProperty,
  { rejectValue: ErrorType }
>('updatePropertyWS', async (socketEvent, { rejectWithValue }) => {
  try {
    const updatePropertyResponse = await api.property.getProperty(socketEvent.entity_id);

    return {
      propertyWS: updatePropertyResponse,
    };
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const addPropertyWS = createAsyncThunk<
  {
    addPropertyWS: PropertyType;
  },
  EntityHubSocket,
  { rejectValue: ErrorType }
>('addPropertyWS', async (socketEvent, { rejectWithValue }) => {
  try {
    const addPropertyResponse = await api.property.getProperty(socketEvent.entity_id);

    return {
      addPropertyWS: addPropertyResponse,
    };
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const acceptedPropertyWS = createAsyncThunk<
  {
    acceptedPropertyWS: PropertyType;
  },
  number,
  { rejectValue: ErrorType }
>('acceptedPropertyWS', async (propertyID, { rejectWithValue }) => {
  try {
    const acceptedPropertyResponse = await api.property.getProperty(propertyID);

    return {
      acceptedPropertyWS: acceptedPropertyResponse,
    };
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});
